import React, { useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { ReactSVG } from "react-svg"
import PropTypes from "prop-types"
import classNames from "classnames"

// ---------------------------------------------------------

import ContentBlock from "@components/content"

// ---------------------------------------------------------

import {
  modal_content,
  modal,
  content_container,
  close_button,
  theme_default,
  theme_off_black,
  theme_video
} from "./styles.module.scss"

// ---------------------------------------------------------

const Modal = (props) => {
  let { className, isOpen, onClose, content, theme = "default", children } = props

  const themeOptions = {
    default: theme_default,
    "off-black": theme_off_black,
    video: theme_video
  }

  const modalRef = useRef() // The modal backdrop
  const modalContentRef = useRef() // Modal dialog box and content

  const handleCloseModal = () => {
    onClose && onClose()
  }

  const handleOutsideClick = (e) => {
    if (modalContentRef.current && !modalContentRef.current.contains(e.target)) {
      handleCloseModal()
    }
  }

  useEffect(() => {
    if (modalRef.current) {
      if (isOpen) {
        modalRef.current.showModal()
        document.body.style.overflow = "hidden"
      } else {
        modalRef.current.close()
        document.body.style.overflow = "auto"
      }
    }
    if (modalContentRef.current) {
      if (isOpen) {
        document.addEventListener("click", handleOutsideClick)
      } else {
        document.removeEventListener("click", handleOutsideClick)
      }
    }
    return () => {
      document.body.style.overflow = "auto"
      document.removeEventListener("click", handleOutsideClick)
    }
  }, [isOpen])

  // ---------------------------------------------------------

  return isOpen
    ? createPortal(
        <dialog
          ref={modalRef}
          className={classNames(className, modal, themeOptions[theme])}
          onCancel={handleCloseModal}
        >
          <div ref={modalContentRef} className={modal_content}>
            <button onClick={handleCloseModal}>
              <ReactSVG className={close_button} src={`/icons/x-mark.svg`} alt="Close modal" />
            </button>
            <ContentBlock className={content_container} content={content} />
            {children}
          </div>
        </dialog>,
        document.getElementById("modal-root")
      )
    : null
}

Modal.propTypes = {
  /**
   * Allows for content to be render in modal as children
   */
  children: PropTypes.object,
  /**
   * Additional class passed down from parent.
   */
  className: PropTypes.string,
  /**
   * HTML string to be rendered to the page.
   */
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Whether or not the modal is open.
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * Function to close the modal.
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Theme of the modal.
   */
  theme: PropTypes.oneOf(["default", "off-black", "video"])
}

export default Modal
